import { AppState } from '../../types';
import AppPaths from '../../AppPaths';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useCartCheckoutV2 from '../../hooks/useCartCheckoutV2';
import { usePathBasedAppCustomization } from '../../hooks/usePathBasedAppCustomization';
import { useCartMode } from '../../lib/cartModes';
import { isPagePath, isShopPath } from '../../lib/shopPath';
import ReferralBanner from '../ReferralBanner/ReferralBanner';
import styles from './AppBar.module.scss';
import ShopPromotionBanner from '../ShopPromotionBanner';
import NavBar from './NavBar';
import { useLocation } from 'react-router-dom';

export default function AppBar() {
  const { showPromotionBanner } = usePathBasedAppCustomization();
  const [navBarOpacity, setNavBarOpacity] = useState<number>(1);
  const [canHideNavBar, setCanHideNavBar] = useState<boolean>(true);
  const { cartSelector } = useCartMode();
  const cart = useSelector(cartSelector);
  const showPromoBannerImprovements = useSelector(
    (state: AppState) => !!state.config.siteConfig.enableEcommPromoBannerImprovements,
  );
  const { pathname } = useLocation();
  // the redesigned checkout has no site nav at all: "back to cart" is the way out of a purchase
  // already begun. the redesigned cart keeps the logo and the bag and drops the menu.
  const cartCheckoutV2 = useCartCheckoutV2();
  const navLessSurface = cartCheckoutV2 && pathname.startsWith(AppPaths.CheckoutStripe);
  const menuLessSurface = cartCheckoutV2 && pathname.startsWith(AppPaths.Bag);

  // The S3 PDP has a custom nav bar, so we don't want to render the default nav bar on that page
  // Only render nav bar on the supplements page if we're not surfacing the subscribe and save iteration
  // of the supplements PDP.
  const renderNavBar = !(
    pathname.includes('products/smart-collar-v3') ||
    pathname.includes('gift-fi') ||
    pathname.includes('redeem') ||
    pathname.includes('vip') ||
    pathname.includes('supplements') ||
    pathname.includes('fi-mini') ||
    pathname.includes('fi-ultra') ||
    navLessSurface
  );

  // The shop and the cart drop the dissolve entirely: fading the bar ends in a collapse to
  // height:0, and because the container is sticky and in flow that removes 96px from above the
  // viewport and lurches the page. On these two the bar just scrolls off and the banner keeps its
  // own sticky - tapping it is what applies the code, and both surfaces quote the applied price.
  const navBarScrollsAway = isShopPath(pathname) || pathname.startsWith(AppPaths.Bag);
  // the listing keeps the bar pinned and solid: no fade, no collapse
  const navBarStaysSolid = isPagePath(pathname, AppPaths.ProductListingPage);

  useEffect(() => {
    // Fade the nav bar out as the user scrolls down so only the promo banner stays pinned.
    //
    // Once the nav bar fades to opacity 0 it collapses to height:0 (see .nav-bar-hidden in
    // AppBar.module.scss). Because .navBarContainer is `position: sticky` and in document flow,
    // that collapse removes the nav bar's height (96px desktop / 80px mobile) from above the
    // viewport, and scroll-anchoring browsers (Chrome/Firefox) compensate by rewriting scrollTop
    // *down* by that same amount. With a single threshold - or a dead band narrower than the nav
    // bar height - that rewrite lands past the toggle point and re-shows the nav bar, which grows
    // the layout back and bumps scrollTop up again: an oscillating loop that flashes the nav bar.
    //
    // Fix: keep the show/hide dead band wider than the collapsed height (here ~160px vs 96px max),
    // so the anchoring rebound always settles *inside* the band and never crosses a threshold.
    // Anchoring then keeps content visually stable (no jump) while the loop is broken. rAF batches
    // the work to at most once per frame regardless of scroll event volume.
    const hideBelow = 180;
    const showBelow = 20;
    let frame: number | null = null;

    const update = () => {
      frame = null;
      const winScroll = document.documentElement.scrollTop || document.body.scrollTop;
      setNavBarOpacity((prev) => {
        if (prev <= 0) {
          // stay hidden until scrolled back near the top
          return winScroll <= showBelow ? 1 : 0;
        }
        const newOpacity = 1 - winScroll / hideBelow;
        return newOpacity <= 0 ? 0 : newOpacity;
      });
    };

    const listenToScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', listenToScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', listenToScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const banners = (
    <>
      {!showPromoBannerImprovements && showPromotionBanner && !cart.referralCode && (
        <header className={classNames(styles.promotion, 'promotionBanner')}>
          <>
            <ShopPromotionBanner />
          </>
        </header>
      )}
      {cart.referralCode && (
        <header className={classNames(styles.promotion, 'promotionBanner', 'referralBanner')}>
          <ReferralBanner />
        </header>
      )}
      {showPromoBannerImprovements && showPromotionBanner && (
        <header className={classNames(styles.promotion, 'promotionBanner')}>
          <>
            <ShopPromotionBanner
              showBannerOnApply={true}
              bannerShownCallback={(shown) =>
                // If promo banner is shown, we can hide the nav bar, otherwise we can't
                setCanHideNavBar(shown)
              }
            />
          </>
        </header>
      )}
    </>
  );

  return (
    <>
      {/* these surfaces pin the banner on its own and let the bar scroll away with the page, so
          the banner is lifted out of the sticky container here: sticky only holds while the
          element's parent box is on screen, and the container's is two rows tall. */}
      {navBarScrollsAway && <div className={styles.stickyBanner}>{banners}</div>}
      <div className={classNames(styles.navBarContainer, { [styles.navBarUnpinned]: navBarScrollsAway })}>
        {!navBarScrollsAway && banners}
        {renderNavBar && (
          <NavBar
            hideMenu={menuLessSurface}
            logoPath={menuLessSurface ? AppPaths.Shop : undefined}
            navBarOpacity={
              !navBarScrollsAway &&
              !navBarStaysSolid &&
              showPromoBannerImprovements &&
              showPromotionBanner &&
              canHideNavBar
                ? navBarOpacity
                : undefined
            }
          />
        )}
      </div>
    </>
  );
}
